import {
  ANUNCIO_SEM_TITULO,
  ROTULO_QUALIFICACAO_ANUNCIO,
  type ChamadoDetalheDto,
  type LeadAnuncioResumoDto,
  type QualificacaoAnuncio,
  type UsuarioSessao,
} from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useQualificarLead } from '../../api/hooks-anuncios'
import { IconeAnuncio } from '../../componentes/icones'
import { juntar } from '../../componentes/ui'
import { formatarDataHora } from '../../formato'
import { pode } from '../../permissao'
import { NomeDoAnuncio } from '../../componentes/NomeDoAnuncio'

/**
 * A faixa logo abaixo do nome, em conversa que começou por anúncio.
 *
 * Só aparece nessas conversas. Nas outras não existe nada ali — nem espaço
 * reservado, nem um "não veio de anúncio" —, porque a maioria das conversas
 * não vem, e uma faixa vazia em todas elas seria ruído no alto da tela mais
 * olhada do sistema.
 */
export function FaixaDeAnuncio({
  chamado,
  usuario,
}: {
  chamado: ChamadoDetalheDto
  usuario: UsuarioSessao
}) {
  const anuncio = chamado.anuncio
  const qualificar = useQualificarLead()
  if (!anuncio) return null

  const podeQualificar = pode(usuario, 'anuncios.editar')
  const erro = qualificar.error instanceof ErroRequisicao ? qualificar.error.corpo.mensagem : null

  function escolher(alvo: Exclude<QualificacaoAnuncio, 'pendente'>, atual: LeadAnuncioResumoDto) {
    // Clicar no que já está escolhido desfaz: é o caminho de volta do clique
    // errado, sem precisar de um terceiro botão ocupando a faixa.
    const nova: QualificacaoAnuncio = atual.qualificacao === alvo ? 'pendente' : alvo
    qualificar.mutate({ id: atual.id, qualificacao: nova, chamadoId: chamado.id })
  }

  const titulo = anuncio.titulo ?? ANUNCIO_SEM_TITULO

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-tinta-200 bg-superficie px-4 py-2">
      <span className="flex min-w-0 items-center gap-1.5 text-sm text-tinta-700">
        <IconeAnuncio className="h-4 w-4 shrink-0 text-marca" />
        <span className="shrink-0 font-medium">Lead de anúncio:</span>
        <NomeDoAnuncio
          titulo={titulo}
          url={anuncio.urlOrigem}
          dica={`chegou em ${formatarDataHora(anuncio.detectadoEm)}`}
        />
      </span>

      <div className="ml-auto flex items-center gap-2">
        {anuncio.qualificacao !== 'pendente' && anuncio.qualificadoPorNome ? (
          <span className="text-xs text-tinta-400">
            {ROTULO_QUALIFICACAO_ANUNCIO[anuncio.qualificacao].toLowerCase()} por{' '}
            {anuncio.qualificadoPorNome}
          </span>
        ) : null}

        {podeQualificar ? (
          <>
            <BotaoDeQualificacao
              cor="verde"
              ativo={anuncio.qualificacao === 'qualificado'}
              desabilitado={qualificar.isPending}
              onClick={() => escolher('qualificado', anuncio)}
            >
              Qualificado
            </BotaoDeQualificacao>
            <BotaoDeQualificacao
              cor="vermelho"
              ativo={anuncio.qualificacao === 'desqualificado'}
              desabilitado={qualificar.isPending}
              onClick={() => escolher('desqualificado', anuncio)}
            >
              Desqualificado
            </BotaoDeQualificacao>
          </>
        ) : (
          <span className="text-xs text-tinta-500">
            {ROTULO_QUALIFICACAO_ANUNCIO[anuncio.qualificacao]}
          </span>
        )}
      </div>

      {erro ? <p className="w-full text-xs text-red-600">{erro}</p> : null}
    </div>
  )
}

/**
 * Cheio quando escolhido, contornado quando não.
 *
 * Os dois botões ficam sempre à vista com a própria cor, para o vendedor saber
 * de relance que há uma decisão a tomar; o preenchimento é o que diz qual foi
 * tomada. Com os dois cheios o tempo todo, não daria para ler a escolha.
 */
function BotaoDeQualificacao({
  cor,
  ativo,
  desabilitado,
  onClick,
  children,
}: {
  cor: 'verde' | 'vermelho'
  ativo: boolean
  desabilitado: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  const estilo =
    cor === 'verde'
      ? ativo
        ? 'bg-green-600 text-white ring-green-600 hover:bg-green-700'
        : 'bg-superficie text-green-700 ring-green-300 hover:bg-green-50'
      : ativo
        ? 'bg-red-600 text-white ring-red-600 hover:bg-red-700'
        : 'bg-superficie text-red-700 ring-red-300 hover:bg-red-50'

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desabilitado}
      aria-pressed={ativo}
      title={ativo ? 'Clique de novo para desfazer' : undefined}
      className={juntar(
        'rounded-full px-3 py-1 text-xs font-semibold ring-1 transition disabled:opacity-50',
        estilo,
      )}
    >
      {children}
    </button>
  )
}
